import { useState } from 'react'
import { X, Plus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

interface PhoneListEditorProps {
  value: string[]
  onChange: (next: string[]) => void
  onValidityChange: (valid: boolean, errors: string[]) => void
  disabled?: boolean
}

const E164_RE = /^\+[1-9]\d{1,14}$/

function isE164(v: string): boolean {
  return E164_RE.test(v.trim())
}

/**
 * Editor amigável para `ghl_test_phone_allowlist` — lista de telefones E.164.
 * Cada número aparece como uma pílula com botão de remoção; o campo de entrada
 * valida o formato antes de adicionar.
 */
export function PhoneListEditor({
  value,
  onChange,
  onValidityChange,
  disabled = false,
}: PhoneListEditorProps): React.ReactElement {
  const [draft, setDraft] = useState('')
  const [inputError, setInputError] = useState<string | null>(null)

  function handleAdd(): void {
    const normalized = draft.trim()
    if (normalized === '') return
    if (!isE164(normalized)) {
      setInputError('Formato inválido. Use E.164: +5511999999999')
      onValidityChange(false, ['Formato inválido'])
      return
    }
    if (value.includes(normalized)) {
      setInputError('Número já está na lista.')
      return
    }
    setInputError(null)
    setDraft('')
    const next = [...value, normalized]
    onChange(next)
    onValidityChange(true, [])
  }

  function handleRemove(phone: string): void {
    onChange(value.filter((p) => p !== phone))
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAdd()
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>): void {
    setDraft(e.target.value)
    if (inputError !== null) setInputError(null)
  }

  return (
    <div className="space-y-3">
      <div className="min-h-[64px] rounded-md border bg-muted/20 p-2">
        {value.length === 0 ? (
          <p className="text-sm text-muted-foreground p-1">
            Nenhum número — lista vazia significa sem restrição (cron processa todos).
          </p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {value.map((phone) => (
              <li
                key={phone}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full border bg-background px-3 py-1',
                  'text-sm font-mono',
                )}
              >
                {phone}
                <button
                  type="button"
                  onClick={() => handleRemove(phone)}
                  disabled={disabled}
                  aria-label={`Remover ${phone}`}
                  className="ml-1 rounded-full opacity-60 hover:opacity-100 focus:outline-none focus:ring-1 focus:ring-ring disabled:pointer-events-none"
                >
                  <X className="h-3 w-3" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="space-y-1">
        <Label htmlFor="phone-list-input">Adicionar número</Label>
        <div className="flex gap-2">
          <Input
            id="phone-list-input"
            value={draft}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="+5511999999999"
            disabled={disabled}
            className={cn('font-mono', inputError !== null && 'border-destructive')}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAdd}
            disabled={disabled || draft.trim() === ''}
            className="shrink-0"
          >
            <Plus className="h-4 w-4 mr-1" />
            Adicionar
          </Button>
        </div>
        {inputError !== null && (
          <p className="text-xs text-destructive">{inputError}</p>
        )}
        <p className="text-xs text-muted-foreground">
          Formato E.164 — ex: <code className="font-mono">+5511999999999</code>. Pressione Enter ou clique em Adicionar.
        </p>
      </div>
    </div>
  )
}
